import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../../theme/tokens';
import { CALC_ROUTE_SET, downstreamCalcs, findCalc } from '../../utils/calcRegistry';
import { isFavorite, toggleFavorite } from '../../utils/favorites';
import { appendHistory, resultSignature } from '../../utils/history';
import { ensureBusReady, latestForSlot, writeBusEntry } from '../../utils/valueBus';
import { SLOT_META, type Slot } from '../../utils/slots';

/* ─── Commit context — fires from anywhere inside a CalcShell ─────────── */

type CommitFn = () => void;
const CommitContext = React.createContext<CommitFn | null>(null);

/**
 * Request that the current calc commits its result — writing to history
 * and to the cross-calc value bus. Safe to call even when no CalcShell
 * is mounted (no-op). NumericField auto-fires this on Enter so every
 * calc gets keyboard-submit → save without per-screen wiring.
 */
export function useCalcShellCommit(): CommitFn {
  const commit = useContext(CommitContext);
  return commit ?? (() => {});
}

export type CalcResultLine = {
  label: string;
  value: string;
  unit?: string;
  /** Primary answer — rendered large as the hero number. */
  emphasis?: boolean;
};

export type CalcShellProps = {
  /**
   * Accent color override — kept for API back-compat.
   * Visually ignored; all screens use the single hi-vis orange token.
   */
  accent?: string;
  title: string;
  /** Short eyebrow tag, e.g. "TAB · Airflow". */
  subtitle?: string;
  /** Formula card body — use <FormulaLine> / <FormulaNote>. */
  formula?: React.ReactNode;
  formulaDefaultCollapsed?: boolean;
  children: React.ReactNode;
  error?: string | null;
  calculateLabel?: string;
  onCalculate?: () => void;
  onClear?: () => void;
  result?: CalcResultLine[] | null;
  copyFooter?: string;
  /** Annotated SVG diagram — shows Calculator/Diagram toggle when provided. */
  diagram?: React.ReactNode;
  /**
   * Values this calc has produced, keyed by canonical slot.
   * Values must be in each slot's Imperial base unit (see SLOT_META).
   * Passing `undefined` for a slot skips it. Written to the value bus
   * whenever the result changes so downstream calcs can prefill.
   */
  outputSlots?: Partial<Record<Slot, number | undefined>>;
  /**
   * Called once on mount (after bus is hydrated) with any values the
   * bus has for this calc's declared `inputs`. Screens decide how to
   * apply them — typically by converting from Imperial to display
   * units and merging into empty fields only.
   */
  onPrefill?: (
    values: Partial<Record<Slot, number>>,
    sources: Partial<Record<Slot, { route: string; title: string }>>,
  ) => void;
};

export function CalcShell({
  title,
  subtitle,
  formula,
  formulaDefaultCollapsed = false,
  children,
  error,
  calculateLabel = 'Calculate',
  onCalculate,
  onClear,
  result,
  copyFooter,
  diagram,
  outputSlots,
  onPrefill,
}: CalcShellProps) {
  const [formulaCollapsed, setFormulaCollapsed] = useState(formulaDefaultCollapsed);
  const [copied, setCopied] = useState(false);
  const [savedPulse, setSavedPulse] = useState(false);
  const [activeTab, setActiveTab] = useState<'calc' | 'diagram'>('calc');

  const route = useRoute();
  const navigation = useNavigation();
  const routeName = route?.name;
  const isKnownCalc = !!routeName && CALC_ROUTE_SET.has(routeName);
  const [pinned, setPinned] = useState(false);

  const registryEntry = useMemo(() => (routeName ? findCalc(routeName) : undefined), [routeName]);
  const downstream = useMemo(
    () => (routeName ? downstreamCalcs(routeName) : []),
    [routeName],
  );
  const [prefillSources, setPrefillSources] = useState<
    Partial<Record<Slot, { route: string; title: string }>>
  >({});

  useEffect(() => {
    if (!isKnownCalc || !routeName) return;
    let mounted = true;
    isFavorite(routeName).then((v) => {
      if (mounted) setPinned(v);
    });
    return () => {
      mounted = false;
    };
  }, [isKnownCalc, routeName]);

  const onTogglePin = async () => {
    if (!isKnownCalc || !routeName) return;
    const next = await toggleFavorite(routeName);
    setPinned(next);
  };

  /* ── Prefill from value bus on mount ─────────────────────────────────── */
  useEffect(() => {
    if (!registryEntry?.inputs?.length || !onPrefill) return;
    let cancelled = false;
    (async () => {
      await ensureBusReady();
      if (cancelled) return;
      const values: Partial<Record<Slot, number>> = {};
      const sources: Partial<Record<Slot, { route: string; title: string }>> = {};
      for (const slot of registryEntry.inputs!) {
        // Don't prefill from a value this very calc wrote — avoid self-loops.
        const latest = latestForSlot(slot);
        if (latest && latest.fromRoute !== routeName) {
          values[slot] = latest.value;
          sources[slot] = { route: latest.fromRoute, title: latest.fromTitle };
        }
      }
      if (Object.keys(values).length > 0) {
        setPrefillSources(sources);
        onPrefill(values, sources);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once per route mount — deliberately not reactive to onPrefill identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryEntry, routeName]);

  /* ── Commit tick — bumped by the Calculate button or by any NumericField
   *   submit (via CommitContext below). Typing alone never commits. ───── */
  const [calcTick, setCalcTick] = useState(0);
  const commit = React.useCallback(() => {
    // Defer so React flushes any setState from the caller's calculate() first.
    setTimeout(() => setCalcTick((t) => t + 1), 0);
  }, []);
  const handleCalculate = onCalculate
    ? () => {
        onCalculate();
        commit();
      }
    : undefined;

  /* ── Write outputs to the bus — only on a committed calculation. ───── */
  useEffect(() => {
    if (calcTick === 0) return;
    if (!routeName || !registryEntry?.title || !outputSlots) return;
    void writeBusEntry(routeName, registryEntry.title, outputSlots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcTick]);

  const dismissPrefillSource = (slot: Slot) => {
    setPrefillSources((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
  };

  const { height: windowHeight } = useWindowDimensions();
  const heroLine = useMemo(() => result?.find((r) => r.emphasis), [result]);
  const subLines = useMemo(() => result?.filter((r) => !r.emphasis) ?? [], [result]);

  // Persist each distinct result to history (per-device) + flash the auto-save dot.
  // Gated on calcTick — only runs when the user hits Calculate, not on every keystroke.
  const lastLoggedSig = React.useRef<string>('');
  useEffect(() => {
    if (calcTick === 0) return;
    if (!isKnownCalc || !routeName) return;
    const sig = resultSignature(result);
    if (!sig || sig === lastLoggedSig.current) return;
    lastLoggedSig.current = sig;
    void appendHistory({
      route: routeName,
      result: (result ?? []).map((r) => ({ label: r.label, value: r.value, unit: r.unit })),
    });
    setSavedPulse(true);
    const t = setTimeout(() => setSavedPulse(false), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcTick]);

  const copyAll = async () => {
    if (!result || result.length === 0) return;
    const lines = result.map((r) => `${r.label}: ${r.value}${r.unit ? ` ${r.unit}` : ''}`);
    const body = lines.join('\n') + (copyFooter ? `\n${copyFooter}` : '');
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(body);
      } else {
        const Clipboard = await import('expo-clipboard');
        await Clipboard.setStringAsync(body);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const hasResult = result && result.length > 0;

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      {/* ── Eyebrow + Title + Pin ── */}
      <View style={styles.header}>
        <View style={styles.headerTopRow}>
          <View style={{ flex: 1 }}>
            {subtitle ? (
              <View style={styles.eyebrowRow}>
                <View style={styles.eyebrowBar} />
                <Text style={styles.eyebrowText}>{subtitle.toUpperCase()}</Text>
              </View>
            ) : null}
            <Text style={styles.title}>{title}</Text>
          </View>
          {isKnownCalc ? (
            <Pressable
              onPress={onTogglePin}
              style={({ pressed }) => [styles.pinBtn, pressed && styles.pressed]}
              accessibilityRole="button"
              accessibilityLabel={pinned ? 'Unpin from home' : 'Pin to home'}
              hitSlop={8}
            >
              <Ionicons
                name={pinned ? 'bookmark' : 'bookmark-outline'}
                size={18}
                color={pinned ? C.accent : C.ink2}
              />
            </Pressable>
          ) : null}
        </View>
      </View>

      {/* ── Diagram toggle ── */}
      {diagram ? (
        <SegmentedButtons
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'calc' | 'diagram')}
          buttons={[
            { value: 'calc', label: 'Calculator' },
            { value: 'diagram', label: 'Diagram' },
          ]}
          style={styles.toggle}
          theme={{
            colors: {
              secondaryContainer: C.accent,
              onSecondaryContainer: '#FFFFFF',
              outline: C.edge,
              onSurface: C.ink2,
              surface: C.panel,
            },
          }}
        />
      ) : null}

      {activeTab === 'diagram' && diagram ? (
        <View style={[styles.diagramContainer, { minHeight: windowHeight * 0.7 }]}>
          {diagram}
        </View>
      ) : (
        <>
          {/* ── Prefill banner — shows when values arrived via the bus ── */}
          {Object.keys(prefillSources).length > 0 ? (
            <View style={styles.prefillBanner}>
              <View style={styles.prefillHeaderRow}>
                <View style={styles.prefillDot} />
                <Text style={styles.prefillHeader}>LINKED VALUES</Text>
              </View>
              {(Object.keys(prefillSources) as Slot[]).map((slot) => {
                const src = prefillSources[slot];
                if (!src) return null;
                return (
                  <View key={slot} style={styles.prefillRow}>
                    <Text style={styles.prefillLabel}>
                      {SLOT_META[slot].label}{' '}
                      <Text style={styles.prefillFrom}>from {src.title}</Text>
                    </Text>
                    <Pressable onPress={() => dismissPrefillSource(slot)} hitSlop={8}>
                      <Text style={styles.prefillDismiss}>×</Text>
                    </Pressable>
                  </View>
                );
              })}
            </View>
          ) : null}

          {/* ── Result hero — above inputs when present ── */}
          {hasResult ? (
            <View style={styles.resultCard}>
              {/* 1px orange rule at the top — design's signature accent on the hero card */}
              <View style={styles.resultTopRule} />

              <View style={styles.resultHeaderRow}>
                <Text style={styles.resultCardLabel}>Result</Text>
                <Pressable onPress={copyAll} style={styles.copyChip}>
                  <Text style={styles.copyChipText}>{copied ? '✓ Copied' : 'Copy'}</Text>
                </Pressable>
              </View>

              {heroLine ? (
                <Text style={styles.heroNumber}>
                  {heroLine.value}
                  {heroLine.unit ? (
                    <Text style={styles.heroUnit}> {heroLine.unit}</Text>
                  ) : null}
                </Text>
              ) : null}
              {heroLine ? <Text style={styles.heroLabel}>{heroLine.label}</Text> : null}

              {subLines.length > 0 ? (
                <>
                  <View style={styles.resultDivider} />
                  <View style={styles.subResultsRow}>
                    {subLines.map((line, i) => (
                      <View key={`${line.label}-${i}`} style={styles.subResultItem}>
                        <Text style={styles.subResultValue}>
                          {line.value}
                          {line.unit ? ` ${line.unit}` : ''}
                        </Text>
                        <Text style={styles.subResultLabel}>{line.label}</Text>
                      </View>
                    ))}
                  </View>
                </>
              ) : null}

              {/* Auto-save indicator — quiet pulse, no toast */}
              <View style={styles.saveStatusRow}>
                <View style={[styles.saveDot, savedPulse && styles.saveDotPulse]} />
                <Text style={styles.saveStatusText}>
                  {savedPulse ? 'saved to history' : 'auto-save'}
                </Text>
              </View>
            </View>
          ) : null}

          {/* ── Formula card ── */}
          {formula ? (
            <View style={styles.formulaCard}>
              <Pressable
                style={styles.formulaHeaderRow}
                onPress={() => setFormulaCollapsed((c) => !c)}
              >
                <Text style={styles.formulaTitle}>Formula</Text>
                <Text style={styles.formulaChevron}>{formulaCollapsed ? '›' : '∨'}</Text>
              </Pressable>
              {!formulaCollapsed ? <View style={styles.formulaBody}>{formula}</View> : null}
            </View>
          ) : null}

          {/* ── Inputs label + children ── */}
          <Text style={styles.sectionLabel}>Inputs</Text>
          <View style={styles.inputs}>
            <CommitContext.Provider value={commit}>{children}</CommitContext.Provider>
          </View>

          {/* ── Error banner ── */}
          {error ? (
            <View style={styles.errorBanner}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          {/* ── Actions ── */}
          <View style={styles.actions}>
            {handleCalculate ? (
              <Pressable
                onPress={handleCalculate}
                style={({ pressed }) => [styles.calcButton, pressed && styles.calcButtonPressed]}
              >
                <Text style={styles.calcButtonLabel}>{calculateLabel}</Text>
              </Pressable>
            ) : null}
            {onClear ? (
              <Pressable onPress={onClear} style={styles.clearButton}>
                <Text style={styles.clearButtonLabel}>Clear</Text>
              </Pressable>
            ) : null}
          </View>

          {/* ── Use in → downstream calc chips ── */}
          {hasResult && downstream.length > 0 ? (
            <View style={styles.useInBlock}>
              <Text style={styles.useInLabel}>USE THESE VALUES IN →</Text>
              <View style={styles.useInRow}>
                {downstream.map((d) => (
                  <Pressable
                    key={d.route}
                    onPress={() => navigation.navigate(d.route as never)}
                    style={({ pressed }) => [styles.useInChip, pressed && styles.pressed]}
                  >
                    <Text style={styles.useInChipText}>{d.title}</Text>
                    <Text style={styles.useInChipArrow}>→</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}
        </>
      )}
    </ScrollView>
  );
}

/* ─── Formula slot helpers ─────────────────────────────────────────────── */

export function FormulaLine({ children }: { children: React.ReactNode }) {
  return <Text style={formulaStyles.line}>{children}</Text>;
}

export function FormulaNote({ children }: { children: React.ReactNode }) {
  return <Text style={formulaStyles.note}>{children}</Text>;
}

const formulaStyles = StyleSheet.create({
  line: {
    fontFamily: FONT.mono,
    fontSize: 15,
    color: C.ink,
    textAlign: 'center',
    marginBottom: 4,
  },
  note: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    textAlign: 'center',
    marginTop: 6,
    lineHeight: 18,
  },
});

/* ─── Styles ───────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: C.bg },
  scrollContent: { padding: SP.md, paddingBottom: SP.xl },

  // Header
  header: { marginBottom: SP.md },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SP.sm,
  },
  pinBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.edge,
    backgroundColor: C.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: { opacity: 0.85 },
  eyebrowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SP.xs,
  },
  eyebrowBar: {
    width: 4,
    height: 11,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginRight: SP.sm,
  },
  eyebrowText: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    letterSpacing: 0.8,
  },
  title: {
    fontFamily: FONT.sansBold,
    fontSize: 26,
    color: C.ink,
    letterSpacing: -0.3,
  },

  // Diagram toggle
  toggle: { marginBottom: SP.md },
  diagramContainer: { alignItems: 'center', paddingVertical: SP.sm },

  // Result hero card
  resultCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.md,
    overflow: 'hidden',
    position: 'relative',
  },
  resultTopRule: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 1,
    backgroundColor: C.accent,
  },
  resultHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SP.sm,
  },
  resultCardLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  copyChip: {
    backgroundColor: C.panel2,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: C.edge,
  },
  copyChipText: { fontFamily: FONT.mono, fontSize: 11, color: C.ink2 },
  heroNumber: { fontFamily: FONT.monoBold, fontSize: 52, color: C.ink, lineHeight: 60 },
  heroUnit: { fontFamily: FONT.mono, fontSize: 20, color: C.ink2 },
  heroLabel: { fontFamily: FONT.sans, fontSize: 13, color: C.ink2, marginTop: 2 },
  resultDivider: { height: 1, backgroundColor: C.edge, marginVertical: SP.sm },
  subResultsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: SP.md },
  subResultItem: { minWidth: 80 },
  subResultValue: { fontFamily: FONT.mono, fontSize: 15, color: C.ink },
  subResultLabel: { fontFamily: FONT.sans, fontSize: 12, color: C.dim, marginTop: 2 },

  // Auto-save indicator
  saveStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm,
    marginTop: SP.sm + 4,
    paddingTop: SP.sm + 4,
    borderTopWidth: 1,
    borderTopColor: C.edge,
  },
  saveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: C.dim,
  },
  saveDotPulse: {
    backgroundColor: C.accent,
  },
  saveStatusText: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.6,
  },

  // Prefill banner (linked values from upstream calc)
  prefillBanner: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    borderLeftWidth: R.stripe,
    borderLeftColor: C.accent,
    paddingHorizontal: SP.md,
    paddingVertical: SP.sm + 2,
    marginBottom: SP.md,
  },
  prefillHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm,
    marginBottom: SP.xs,
  },
  prefillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: C.accent,
  },
  prefillHeader: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.8,
  },
  prefillRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  prefillLabel: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink,
    flex: 1,
  },
  prefillFrom: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
  },
  prefillDismiss: {
    fontFamily: FONT.mono,
    fontSize: 18,
    color: C.dim,
    paddingHorizontal: SP.sm,
  },

  // "Use in →" downstream chips
  useInBlock: {
    marginTop: SP.lg,
    marginBottom: SP.md,
  },
  useInLabel: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.8,
    marginBottom: SP.sm,
  },
  useInRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SP.sm,
  },
  useInChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: SP.sm + 4,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: C.accent,
    backgroundColor: C.panel,
  },
  useInChipText: {
    fontFamily: FONT.sansMed,
    fontSize: 13,
    color: C.ink,
  },
  useInChipArrow: {
    fontFamily: FONT.mono,
    fontSize: 14,
    color: C.accent,
  },

  // Formula card
  formulaCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    borderLeftWidth: R.stripe,
    borderLeftColor: C.accent,
    marginBottom: SP.md,
    overflow: 'hidden',
  },
  formulaHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SP.md,
    paddingVertical: SP.sm + 2,
  },
  formulaTitle: {
    fontFamily: FONT.sansMed,
    fontSize: 13,
    color: C.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  formulaChevron: { fontFamily: FONT.mono, fontSize: 14, color: C.dim },
  formulaBody: { paddingHorizontal: SP.md, paddingBottom: SP.md },

  // Inputs
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
  inputs: { marginBottom: SP.md, gap: SP.sm },

  // Error
  errorBanner: {
    backgroundColor: 'rgba(255,69,58,0.1)',
    borderColor: 'rgba(255,69,58,0.35)',
    borderWidth: 1,
    borderRadius: R.field,
    padding: SP.sm + 4,
    marginBottom: SP.sm,
  },
  errorText: { fontFamily: FONT.sans, fontSize: 14, color: C.danger },

  // Actions
  actions: { flexDirection: 'row', gap: SP.sm, marginTop: SP.xs },
  calcButton: {
    flex: 1,
    backgroundColor: C.accent,
    borderRadius: R.button,
    paddingVertical: 16,
    alignItems: 'center',
  },
  calcButtonPressed: { opacity: 0.85 },
  calcButtonLabel: { fontFamily: FONT.sansBold, fontSize: 16, color: '#FFFFFF' },
  clearButton: { paddingVertical: 16, paddingHorizontal: SP.md, alignItems: 'center' },
  clearButtonLabel: { fontFamily: FONT.sans, fontSize: 16, color: C.ink2 },
});
